import { messaging } from "./firebase";
import { onMessage } from "firebase/messaging";
import { useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Icons from "@/assets";
import { useDispatch } from "react-redux";
import {
  incrementUnreadCount,
  triggerRefresh,
  setLastReceivedNotification,
  setRefetchFlag,
} from "@/store/slices/notificationSlice";
import { NotificationPayload } from "@/types/common";

export const useNotificationListener = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleNotification = useCallback(
    (payload: NotificationPayload) => {
      console.log("Handling notification:", payload);
      dispatch(incrementUnreadCount());
      dispatch(setLastReceivedNotification(payload));
      dispatch(triggerRefresh());
      dispatch(setRefetchFlag(true));

      const { title, body, route, is_best, status } = payload.data || {};

      const showToast = (
        toastTitle: string,
        toastBody: string,
        icon: string,
      ) => {
        toast(
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              cursor: "pointer",
            }}
            onClick={() => {
              if (route) navigate(route);
            }}
          >
            <img
              src={icon}
              alt="Notification Icon"
              style={{
                width: "40px",
                height: "40px",
                marginRight: "12px",
                objectFit: "contain",
              }}
            />
            <div>
              <strong
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "14px",
                }}
              >
                {toastTitle}
              </strong>
              <div style={{ fontSize: "12px", color: "#666" }}>{toastBody}</div>
            </div>
          </div>,
          {
            icon: false,
          },
        );
      };

      // Show toast notification
      if (is_best === "true" && status === "reviewed") {
        // Show Congratulations Toast First
        console.log("Showing congratulations toast");
        showToast(
          "Congratulations!",
          `Your contribution has been Reviewed and successfully added to the ${title} details.`,
          Icons.cupIcon,
        );
        // Show Regular Toast second with slight delay
        setTimeout(() => {
          console.log("Showing regular toast after delay");
          showToast(title || "", body || "", Icons.WheelChair);
        }, 500);
      } else {
        console.log("Showing standard toast");
        showToast(title || "", body || "", Icons.WheelChair);
      }
    },
    [navigate, dispatch],
  );

  // Use a ref to always point to the latest handleNotification without triggering re-subscriptions
  const handlerRef = useRef(handleNotification);

  useEffect(() => {
    handlerRef.current = handleNotification;
  }, [handleNotification]);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log(
        "Foreground notification received using listener hook:",
        payload,
      );
      handlerRef.current(payload);
    });

    return () => unsubscribe();
  }, []); // Only subscribe once on mount

  useEffect(() => {
    const channel = new BroadcastChannel("notification_channel");
    channel.onmessage = (event) => {
      if (event.data.type === "BACKGROUND_NOTIFICATION_RECEIVED") {
        console.log(
          "Received background notification broadcast:",
          event.data.payload,
        );
        handlerRef.current(event.data.payload);
      }
    };

    return () => {
      channel.close();
    };
  }, []); // Only subscribe once on mount
};
